import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { getRepRatingTier } from '../utils/iconResolver';
import {
  sendTicketOpportunity,
  fetchOpportunityExtras,
  sendTicketReminder,
  updateRepRateOverrides,
  sendManualInvite,
} from '../services/api';
import {
  parseOpportunityHistory,
  parsePreferredTechs,
  parseNearbyReps,
  isClientBlocked,
} from '../utils/opportunityQualify';
import './OpportunityPanel.css';

export { parseOpportunityHistory };

const REMINDER_COOLDOWN_MS = 8 * 60 * 60 * 1000;

const SORT_PRESETS = {
  'distance-type-rating': {
    label: 'Distance, Type, Rating',
    rules: [
      { field: 'dist', dir: 'asc' },
      { field: 'paySortVal', dir: 'asc' },
      { field: 'rating', dir: 'desc' },
    ],
  },
  'type-distance-rating': {
    label: 'Type, Distance, Rating',
    rules: [
      { field: 'paySortVal', dir: 'asc' },
      { field: 'dist', dir: 'asc' },
      { field: 'rating', dir: 'desc' },
    ],
  },
  'rating-distance-type': {
    label: 'Rating, Distance, Type',
    rules: [
      { field: 'rating', dir: 'desc' },
      { field: 'dist', dir: 'asc' },
      { field: 'paySortVal', dir: 'asc' },
    ],
  },
  'pay-distance-rating': {
    label: 'Pay, Distance, Rating',
    rules: [
      { field: 'payTypeVal', dir: 'asc' },
      { field: 'dist', dir: 'asc' },
      { field: 'rating', dir: 'desc' },
    ],
  },
  'experience-rating-distance': {
    label: 'Experience, Rating, Distance',
    rules: [
      { field: 'projectJobs', dir: 'desc' },
      { field: 'totalJobs', dir: 'desc' },
      { field: 'rating', dir: 'desc' },
      { field: 'dist', dir: 'asc' },
    ],
  },
};

const DEFAULT_FILTERS = {
  payment: 'all',
  distance: 'all',
  rating: 'all',
  invited: 'show',
  fnReadiness: 'show',
  rate: 'all',
};

function parseRepRateList(str) {
  const s = new Set();
  if (!str) return s;
  String(str).split(',').forEach(p => {
    const id = parseInt(String(p).trim(), 10);
    if (!Number.isNaN(id)) s.add(id);
  });
  return s;
}

function paymentTierRank(rep) {
  const pm = rep.paymentMethod;
  const coi = rep.coiCompliant === true;
  if (pm === 'Direct Deposit' && coi) return 1;
  if (pm === 'Direct Deposit' || pm === 'Direct Deposit - Need COI') return 2;
  if (pm === 'FN') return 3;
  return 4;
}

function paymentBucket(rep) {
  const pm = rep.paymentMethod || '';
  const coi = rep.coiCompliant === true;
  if (pm === 'Direct Deposit' && coi) return 'dd-coi';
  if (pm === 'Direct Deposit' || pm === 'Direct Deposit - Need COI') return 'dd';
  if (pm === 'FN') return 'fn';
  return 'unknown';
}

function PaymentBadge({ rep }) {
  const bucket = paymentBucket(rep);
  if (bucket === 'dd-coi') return <span className="opp-pay opp-pay-dd-coi">DD-COI</span>;
  if (bucket === 'dd') return <span className="opp-pay opp-pay-dd-nocoi">DD</span>;
  if (bucket === 'fn') return <span className="opp-pay opp-pay-fn">FN</span>;
  return <span className="opp-pay opp-pay-unknown">—</span>;
}

function ComplianceBar({ html }) {
  if (!html) return <span className="opp-na">—</span>;
  return <span className="compliance-bar" dangerouslySetInnerHTML={{ __html: html }} />;
}

function renderStars(rating) {
  const { tier, color } = getRepRatingTier(rating);
  if (tier === 0) return <span className="rating-stars no-rating">No Rating</span>;
  return (
    <span className="rating-stars" aria-label={`${tier} of 5 stars`}>
      <span className="rating-stars-filled" style={{ color }}>{'★'.repeat(tier)}</span>
      <span className="rating-stars-empty">{'☆'.repeat(5 - tier)}</span>
    </span>
  );
}

function fmtCurrency(val) {
  return `$${Math.round(val || 0)}`;
}

/**
 * Pay cell driven by ticket pay type (FID 790), matching QB code page:
 * Fixed / Fixed Rate → Min Fee; Hourly → Hourly Rate2; Blended → First/Hourly.
 */
function payTypeInfo(rep, ticketPayType) {
  const t = String(ticketPayType || '').toLowerCase().trim();
  if (t === 'fixed' || t === 'fixed rate') {
    const v = rep.defaultMinFeePerVisit || 0;
    return v
      ? { label: fmtCurrency(v), eligible: true, sortVal: v }
      : { label: '—', eligible: false, sortVal: null };
  }
  if (t === 'hourly') {
    const v = rep.defaultHourlyRate || 0;
    return v
      ? { label: `${fmtCurrency(v)}/hr`, eligible: true, sortVal: v }
      : { label: '—', eligible: false, sortVal: null };
  }
  if (t === 'blended') {
    const hr = rep.defaultHourlyRate || 0;
    const fh = rep.defaultFirstHour || hr;
    return (fh && hr)
      ? { label: `${fmtCurrency(fh)}/${fmtCurrency(hr)}`, eligible: true, sortVal: fh }
      : { label: '—', eligible: false, sortVal: null };
  }
  return { label: '—', eligible: false, sortVal: null };
}

function fmtCooldownLeft(lastMs, cooldownMs = REMINDER_COOLDOWN_MS) {
  const left = cooldownMs - (Date.now() - lastMs);
  if (left <= 0) return '';
  const h = Math.floor(left / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function compareByRule(a, b, rule) {
  const dir = rule.dir || 'asc';
  let va = a[rule.field];
  let vb = b[rule.field];
  if (rule.field === 'dist') {
    const aUnknown = va === null || va === undefined;
    const bUnknown = vb === null || vb === undefined;
    if (aUnknown && bUnknown) return 0;
    if (aUnknown) return 1;
    if (bUnknown) return -1;
  }
  if (va === null || va === undefined) va = dir === 'asc' ? Infinity : -Infinity;
  if (vb === null || vb === undefined) vb = dir === 'asc' ? Infinity : -Infinity;
  if (typeof va === 'string' || typeof vb === 'string') {
    return dir === 'asc'
      ? String(va).localeCompare(String(vb))
      : String(vb).localeCompare(String(va));
  }
  return dir === 'asc' ? va - vb : vb - va;
}

function normalizeSearch(val) {
  return String(val ?? '').toLowerCase().trim();
}

function digitsOnly(val) {
  return String(val ?? '').replace(/\D/g, '');
}

function OpportunityPanel({ ticket, fieldReps, onTicketUpdated }) {
  const [sortPreset, setSortPreset] = useState('distance-type-rating');
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterState, setFilterState] = useState({ ...DEFAULT_FILTERS });
  const [sentRepIds, setSentRepIds] = useState(() => new Set());
  const [sendingRepId, setSendingRepId] = useState(null);
  const [errorByRepId, setErrorByRepId] = useState({});
  const [reminders, setReminders] = useState({});
  const [projectJobCounts, setProjectJobCounts] = useState({});
  const [cooldownMs, setCooldownMs] = useState(REMINDER_COOLDOWN_MS);
  const [extrasLoading, setExtrasLoading] = useState(false);
  const [remindingRepId, setRemindingRepId] = useState(null);

  // Pay Rep Rates — UI enabled set vs persisted FID 1742 set
  const [overrideRepPay, setOverrideRepPay] = useState(() => ticket.overrideRepPay === true);
  const [repRatePersisted, setRepRatePersisted] = useState(() => parseRepRateList(ticket.repRateOverrideList));
  const [repRateEnabled, setRepRateEnabled] = useState(() => new Set());

  const [manualOpen, setManualOpen] = useState(false);
  const [manualQuery, setManualQuery] = useState('');
  const [manualSendingId, setManualSendingId] = useState(null);
  const [manualError, setManualError] = useState('');
  const manualInputRef = useRef(null);

  const fnReady = (ticket.fnReadiness || '').toString().toLowerCase().includes('ready');
  const clientName = (ticket.clientName || '').trim().toLowerCase();
  const ticketPayType = ticket.payType || '';

  const historyInvited = useMemo(
    () => parseOpportunityHistory(ticket.opportunityHistory),
    [ticket.opportunityHistory]
  );

  const preferredMap = useMemo(
    () => parsePreferredTechs(ticket.preferredTechsJson),
    [ticket.preferredTechsJson]
  );

  const nearby = useMemo(
    () => parseNearbyReps(ticket.nearbyFieldRepsJson),
    [ticket.nearbyFieldRepsJson]
  );

  const repsById = useMemo(() => {
    const m = new Map();
    fieldReps.forEach(r => m.set(r.repId, r));
    return m;
  }, [fieldReps]);

  // Sync pay-rate state when ticket cache updates
  useEffect(() => {
    setOverrideRepPay(ticket.overrideRepPay === true);
    setRepRatePersisted(parseRepRateList(ticket.repRateOverrideList));
  }, [ticket.overrideRepPay, ticket.repRateOverrideList, ticket.recordId]);

  const invitedSet = useMemo(() => {
    const s = new Set(historyInvited);
    sentRepIds.forEach(id => s.add(id));
    return s;
  }, [historyInvited, sentRepIds]);

  // Build candidate list: nearby + extra invited + preferred not already present
  const candidates = useMemo(() => {
    const nearbyIdSet = new Set(nearby.map(r => r.id).filter(Boolean));
    const extraInvited = [...invitedSet]
      .filter(id => !nearbyIdSet.has(id))
      .map(id => ({ id, dist: null, manual: true }));
    const presentIds = new Set([...nearbyIdSet, ...extraInvited.map(r => r.id)]);
    const extraPreferred = [...preferredMap.keys()]
      .filter(id => !presentIds.has(id))
      .map(id => ({ id, dist: null, manual: true }));

    const displayList = [...nearby, ...extraInvited, ...extraPreferred];

    return displayList.map(({ id, dist, manual }) => {
      const rep = repsById.get(id);
      if (!rep) {
        // Prefer showing a stub for invited/preferred even if not in cache
        if (!invitedSet.has(id) && !preferredMap.has(id)) return null;
        const stub = {
          repId: id,
          name: `Rep #${id}`,
          paymentMethod: '',
          coiCompliant: false,
          rating: 0,
          phone: '',
          defaultMinFeePerVisit: 0,
          defaultHourlyRate: 0,
          defaultFirstHour: 0,
          last12Months: 0,
          otrResAbdnHtml: '',
          noInviting: false,
        };
        const pay = payTypeInfo(stub, ticketPayType);
        return {
          ...stub,
          dist: Number.isFinite(dist) ? dist : null,
          manualDistance: manual || !Number.isFinite(dist),
          preferred: preferredMap.has(id),
          prefTravel: preferredMap.get(id) ?? null,
          fnBlocked: false,
          projectJobs: projectJobCounts[id] || 0,
          totalJobs: 0,
          paySortVal: 4,
          payEligible: pay.eligible,
          payTypeVal: pay.sortVal,
          payLabel: pay.label,
        };
      }
      if (rep.noInviting === true) return null;
      if (isClientBlocked(rep, clientName)) return null;
      const fnBlocked = !fnReady && rep.paymentMethod === 'FN';
      const pay = payTypeInfo(rep, ticketPayType);
      return {
        ...rep,
        dist: Number.isFinite(dist) ? dist : null,
        manualDistance: manual === true || !Number.isFinite(dist),
        preferred: preferredMap.has(id),
        prefTravel: preferredMap.get(id) ?? null,
        fnBlocked,
        projectJobs: projectJobCounts[id] || 0,
        totalJobs: rep.last12Months || 0,
        paySortVal: paymentTierRank(rep),
        payEligible: pay.eligible,
        payTypeVal: pay.sortVal,
        payLabel: pay.label,
      };
    }).filter(Boolean);
  }, [
    nearby, repsById, clientName, fnReady, invitedSet, preferredMap,
    ticketPayType, projectJobCounts,
  ]);

  // Seed Pay Rep Rates toggles from ticket FIDs (stable key so proj-job refresh
  // doesn't wipe in-progress toggle state).
  const eligibilityKey = useMemo(
    () => candidates.map(c => `${c.repId}:${c.payEligible ? 1 : 0}`).join(','),
    [candidates]
  );

  useEffect(() => {
    const next = new Set();
    candidates.forEach(r => {
      if (r.payEligible && (overrideRepPay || repRatePersisted.has(r.repId))) {
        next.add(r.repId);
      }
    });
    setRepRateEnabled(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibilityKey, overrideRepPay, repRatePersisted]);

  // Load reminders + project job counts when panel mounts / candidate IDs change
  useEffect(() => {
    let cancelled = false;
    const ids = candidates.map(c => c.repId);
    if (!ids.length) {
      setReminders({});
      setProjectJobCounts({});
      return undefined;
    }
    setExtrasLoading(true);
    fetchOpportunityExtras(ticket.recordId, ids)
      .then(data => {
        if (cancelled) return;
        setReminders(data.reminders || {});
        setProjectJobCounts(data.projectJobCounts || {});
        if (data.reminderCooldownMs) setCooldownMs(data.reminderCooldownMs);
      })
      .catch(err => console.warn('Opportunity extras failed:', err.message))
      .finally(() => { if (!cancelled) setExtrasLoading(false); });
    return () => { cancelled = true; };
    // Intentionally key off recordId + nearby/history/preferred shape, not full candidates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.recordId, ticket.nearbyFieldRepsJson, ticket.opportunityHistory, ticket.preferredTechsJson]);

  const activeFilterCount = useMemo(
    () => Object.keys(DEFAULT_FILTERS).reduce(
      (n, k) => n + (filterState[k] !== DEFAULT_FILTERS[k] ? 1 : 0),
      0
    ),
    [filterState]
  );

  const visible = useMemo(() => {
    const q = normalizeSearch(searchTerm);
    const qDigits = digitsOnly(q);
    return candidates.filter(rep => {
      if (q) {
        const hay = [rep.repId, rep.name, rep.phone, rep.email, rep.paymentMethod]
          .map(normalizeSearch).join(' ');
        const matchText = hay.includes(q);
        const matchPhone = qDigits.length > 0 && digitsOnly(rep.phone).includes(qDigits);
        if (!matchText && !matchPhone) return false;
      }
      if (filterState.payment !== 'all' && paymentBucket(rep) !== filterState.payment) return false;
      if (filterState.distance !== 'all') {
        if (rep.dist === null || rep.dist === undefined) return false;
        if (rep.dist > parseFloat(filterState.distance)) return false;
      }
      if (filterState.rating !== 'all' && (rep.rating || 0) < parseFloat(filterState.rating)) return false;
      if (filterState.invited === 'hide' && invitedSet.has(rep.repId)) return false;
      if (filterState.fnReadiness === 'hide-blocked' && rep.fnBlocked) return false;
      if (filterState.rate === 'has-rate' && !rep.payEligible) return false;
      return true;
    });
  }, [candidates, searchTerm, filterState, invitedSet]);

  const sorted = useMemo(() => {
    const rules = SORT_PRESETS[sortPreset]?.rules || SORT_PRESETS['distance-type-rating'].rules;
    return [...visible].sort((a, b) => {
      for (const rule of rules) {
        const cmp = compareByRule(a, b, rule);
        if (cmp !== 0) return cmp;
      }
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [visible, sortPreset]);

  const persistRepRates = useCallback(async (enabledSet, overrideFlag, invited) => {
    // Only commit IDs that are enabled AND already invited (QB deferred-write rule)
    const persisted = new Set();
    enabledSet.forEach(id => {
      if (invited.has(id)) persisted.add(id);
    });
    // Keep previously persisted IDs that are still enabled
    repRatePersisted.forEach(id => {
      if (enabledSet.has(id)) persisted.add(id);
    });
    // Drop any that were turned off
    const list = [...persisted].join(',');
    const result = await updateRepRateOverrides(ticket.recordId, {
      repRateOverrideList: list,
      overrideRepPay: overrideFlag,
    });
    setRepRatePersisted(persisted);
    setOverrideRepPay(overrideFlag);
    if (onTicketUpdated && result.ticket) onTicketUpdated(result.ticket);
    return persisted;
  }, [ticket.recordId, repRatePersisted, onTicketUpdated]);

  const handleRepRateToggle = async (repId, turnOn) => {
    const rep = candidates.find(r => r.repId === repId);
    if (!rep || !rep.payEligible) return;

    const nextEnabled = new Set(repRateEnabled);
    let nextOverride = overrideRepPay;

    if (turnOn) {
      nextEnabled.add(repId);
    } else {
      nextEnabled.delete(repId);
      if (overrideRepPay) nextOverride = false;
    }
    setRepRateEnabled(nextEnabled);
    setOverrideRepPay(nextOverride);

    // Persist immediately when turning off, or when turning on for already-sent reps
    const needsWrite = !turnOn || invitedSet.has(repId) || nextOverride !== overrideRepPay;
    if (!needsWrite && turnOn && !invitedSet.has(repId)) return;

    try {
      await persistRepRates(nextEnabled, nextOverride, invitedSet);
    } catch (err) {
      console.error('Rep rate toggle failed:', err);
      setErrorByRepId(prev => ({ ...prev, [repId]: err.message || 'Rate toggle failed' }));
    }
  };

  const handleToggleAllRates = async () => {
    const eligible = candidates.filter(r => r.payEligible);
    if (!eligible.length) return;
    const allOn = eligible.every(r => repRateEnabled.has(r.repId));
    const nextEnabled = new Set(repRateEnabled);
    let nextOverride;

    if (allOn) {
      eligible.forEach(r => nextEnabled.delete(r.repId));
      nextOverride = false;
    } else {
      eligible.forEach(r => nextEnabled.add(r.repId));
      nextOverride = true;
    }
    setRepRateEnabled(nextEnabled);
    setOverrideRepPay(nextOverride);
    try {
      await persistRepRates(nextEnabled, nextOverride, invitedSet);
    } catch (err) {
      console.error('Toggle-all rates failed:', err);
    }
  };

  const handleSend = async (repId) => {
    if (sendingRepId !== null) return;
    setSendingRepId(repId);
    setErrorByRepId(prev => {
      const next = { ...prev };
      delete next[repId];
      return next;
    });
    try {
      await sendTicketOpportunity(ticket.recordId, repId);
      const nextSent = new Set(sentRepIds).add(repId);
      setSentRepIds(nextSent);
      const nextInvited = new Set(invitedSet).add(repId);

      // Commit Pay Rep Rates if toggle was on
      if (repRateEnabled.has(repId) && !repRatePersisted.has(repId)) {
        try {
          await persistRepRates(repRateEnabled, overrideRepPay, nextInvited);
        } catch (err) {
          console.error('Rep rate append on send failed:', err);
        }
      }
    } catch (err) {
      setErrorByRepId(prev => ({ ...prev, [repId]: err.message || 'Send failed' }));
    } finally {
      setSendingRepId(null);
    }
  };

  const handleReminder = async (repId) => {
    if (remindingRepId !== null) return;
    const d = reminders[repId] || { count: 0, lastMs: 0 };
    if (d.count > 0 && (Date.now() - d.lastMs) < cooldownMs) return;

    setRemindingRepId(repId);
    try {
      await sendTicketReminder(ticket.recordId, repId);
      setReminders(prev => ({
        ...prev,
        [repId]: { count: (prev[repId]?.count || 0) + 1, lastMs: Date.now() },
      }));
    } catch (err) {
      setErrorByRepId(prev => ({ ...prev, [repId]: err.message || 'Reminder failed' }));
    } finally {
      setRemindingRepId(null);
    }
  };

  const manualResults = useMemo(() => {
    const q = normalizeSearch(manualQuery);
    if (!q && !/^\d+$/.test(manualQuery.trim())) return [];
    const qDigits = digitsOnly(q);
    return fieldReps
      .filter(r => {
        if (r.noInviting === true) return false;
        if (/^\d+$/.test(manualQuery.trim())) {
          return r.repId === parseInt(manualQuery.trim(), 10);
        }
        if (q.length < 2) return false;
        const hay = [r.repId, r.name, r.phone, r.email].map(normalizeSearch).join(' ');
        if (hay.includes(q)) return true;
        return qDigits.length > 0 && digitsOnly(r.phone).includes(qDigits);
      })
      .slice(0, 25);
  }, [fieldReps, manualQuery]);

  const manualBlockReason = (rep) => {
    if (invitedSet.has(rep.repId)) return 'Already Sent';
    if (rep.noInviting) return 'No Inviting';
    if (isClientBlocked(rep, clientName)) return 'Client Blocked';
    if (!fnReady && rep.paymentMethod === 'FN') return 'FN Not Ready';
    const isDD = /direct deposit/i.test(rep.paymentMethod || '');
    const coiOK = rep.coiCompliant === true;
    if (isDD && !coiOK) return 'Requires COI';
    return '';
  };

  const handleManualInvite = async (repId) => {
    const rep = fieldReps.find(r => r.repId === repId);
    if (!rep || manualBlockReason(rep)) return;
    setManualSendingId(repId);
    setManualError('');
    try {
      const result = await sendManualInvite(ticket.recordId, repId);
      setSentRepIds(prev => new Set(prev).add(repId));
      if (onTicketUpdated && result.ticket) onTicketUpdated(result.ticket);
      setManualError('');
    } catch (err) {
      setManualError(err.message || 'Manual invite failed');
    } finally {
      setManualSendingId(null);
    }
  };

  useEffect(() => {
    if (manualOpen) {
      setTimeout(() => manualInputRef.current?.focus(), 50);
    }
  }, [manualOpen]);

  const eligibleRateReps = sorted.filter(r => r.payEligible);
  const allRatesOn = eligibleRateReps.length > 0
    && eligibleRateReps.every(r => repRateEnabled.has(r.repId));
  const hasControls = searchTerm.trim() || activeFilterCount > 0;
  const countText = hasControls
    ? `${sorted.length} of ${candidates.length} reps`
    : `${candidates.length} reps`;

  return (
    <div className="opportunity-panel">
      <div className="opp-toolbar">
        <div className="opp-search-wrap">
          <input
            className="opp-search"
            type="search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search name, phone, email, ID"
          />
          {searchTerm && (
            <button type="button" className="opp-clear-search" onClick={() => setSearchTerm('')}>
              Clear
            </button>
          )}
        </div>
        <div className="opp-toolbar-actions">
          <label className="opp-sort-label">
            Sort
            <select
              className="opp-sort"
              value={sortPreset}
              onChange={e => setSortPreset(e.target.value)}
            >
              {Object.entries(SORT_PRESETS).map(([key, p]) => (
                <option key={key} value={key}>{p.label}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="opp-manual-btn"
            onClick={() => { setManualOpen(true); setManualQuery(''); setManualError(''); }}
          >
            Manual Invite
          </button>
          <button
            type="button"
            className={`opp-filter-toggle${filtersOpen || activeFilterCount ? ' active' : ''}`}
            onClick={() => setFiltersOpen(o => !o)}
          >
            Filters
            {activeFilterCount > 0 && <span className="opp-filter-count">{activeFilterCount}</span>}
          </button>
          <span className="opp-count">{countText}{extrasLoading ? '…' : ''}</span>
        </div>
      </div>

      {filtersOpen && (
        <div className="opp-filter-panel">
          <label>
            <span>Pay Type</span>
            <select
              value={filterState.payment}
              onChange={e => setFilterState(s => ({ ...s, payment: e.target.value }))}
            >
              <option value="all">All</option>
              <option value="dd-coi">DD + COI</option>
              <option value="dd">DD</option>
              <option value="fn">FN</option>
              <option value="unknown">Unknown</option>
            </select>
          </label>
          <label>
            <span>Distance</span>
            <select
              value={filterState.distance}
              onChange={e => setFilterState(s => ({ ...s, distance: e.target.value }))}
            >
              <option value="all">Any</option>
              <option value="10">10 mi</option>
              <option value="25">25 mi</option>
              <option value="50">50 mi</option>
              <option value="100">100 mi</option>
            </select>
          </label>
          <label>
            <span>Rating</span>
            <select
              value={filterState.rating}
              onChange={e => setFilterState(s => ({ ...s, rating: e.target.value }))}
            >
              <option value="all">Any</option>
              <option value="3">3+ stars</option>
              <option value="4">4+ stars</option>
              <option value="4.5">4.5+ stars</option>
            </select>
          </label>
          <label>
            <span>Invited</span>
            <select
              value={filterState.invited}
              onChange={e => setFilterState(s => ({ ...s, invited: e.target.value }))}
            >
              <option value="show">Show sent</option>
              <option value="hide">Hide sent</option>
            </select>
          </label>
          <label>
            <span>FN Status</span>
            <select
              value={filterState.fnReadiness}
              onChange={e => setFilterState(s => ({ ...s, fnReadiness: e.target.value }))}
            >
              <option value="show">Show all</option>
              <option value="hide-blocked">Hide blocked</option>
            </select>
          </label>
          <label>
            <span>Rate</span>
            <select
              value={filterState.rate}
              onChange={e => setFilterState(s => ({ ...s, rate: e.target.value }))}
            >
              <option value="all">Any</option>
              <option value="has-rate">Has rate</option>
            </select>
          </label>
          <button
            type="button"
            className="opp-reset-filters"
            disabled={activeFilterCount === 0}
            onClick={() => setFilterState({ ...DEFAULT_FILTERS })}
          >
            Reset
          </button>
        </div>
      )}

      {!fnReady && (
        <div className="opp-note">
          FN Readiness: Not Ready —{' '}
          <a
            href={`https://siteservicesnow.quickbase.com/db/bjwdgf796?a=dr&rid=${ticket.recordId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="opp-note-link"
          >
            Open Ticket #{ticket.recordId}
          </a>
        </div>
      )}

      {ticketPayType && (
        <div className="opp-pay-type-hint">
          Ticket pay type: <strong>{ticketPayType}</strong>
          {' · '}
          <button
            type="button"
            className={`opp-rate-master${allRatesOn ? ' all-on' : ''}`}
            onClick={handleToggleAllRates}
            title="Toggle Pay Rep Rates for every eligible rep"
          >
            Pay Rep Rates {allRatesOn ? 'ON' : 'OFF'}
          </button>
        </div>
      )}

      {sorted.length === 0 && (
        <div className="opp-empty">
          {hasControls
            ? 'No reps match your search or filters.'
            : 'No eligible nearby field reps — use Manual Invite above.'}
        </div>
      )}

      {sorted.map(rep => {
        const alreadySent = invitedSet.has(rep.repId);
        const isSending = sendingRepId === rep.repId;
        const error = errorByRepId[rep.repId];
        const rateOn = repRateEnabled.has(rep.repId);
        const rs = alreadySent ? (reminders[rep.repId] || { count: 0, lastMs: 0 }) : null;
        const onCooldown = rs && rs.count > 0 && (Date.now() - rs.lastMs) < cooldownMs;
        const isReminding = remindingRepId === rep.repId;

        return (
          <div key={rep.repId} className={`opp-card${rep.fnBlocked ? ' opp-card-blocked' : ''}`}>
            <div className="opp-card-row1">
              <a
                href={`https://siteservicesnow.quickbase.com/db/bjt99p7ie?a=dr&rid=${rep.repId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="opp-rep-name"
              >
                {rep.name || `Rep #${rep.repId}`}
              </a>
              {rep.preferred && (
                <span className="opp-pref-pill" title="Preferred tech for this site">
                  ★ Preferred{rep.prefTravel != null ? ` · $${rep.prefTravel}` : ''}
                </span>
              )}
              <span className="opp-rep-id">#{rep.repId}</span>
              <span className="opp-rep-dist">
                {rep.dist === null || rep.dist === undefined
                  ? 'Manual'
                  : `${rep.dist.toFixed(1)} mi`}
              </span>
            </div>

            <div className="opp-card-row2">
              <span className="opp-row-left">
                <span
                  className={`opp-rep-rate${rateOn ? ' rate-on' : ''}`}
                  title={ticketPayType ? `Ticket pay type: ${ticketPayType}` : 'No ticket pay type set'}
                >
                  {rep.payLabel}
                </span>
                <label
                  className={`opp-rate-switch-wrap${!rep.payEligible ? ' disabled' : ''}`}
                  title={rep.payEligible ? 'Pay Rep Rates' : 'Missing rate for this pay type'}
                >
                  <input
                    type="checkbox"
                    className="opp-rate-switch"
                    checked={rateOn}
                    disabled={!rep.payEligible}
                    onChange={e => handleRepRateToggle(rep.repId, e.target.checked)}
                  />
                  <span className="opp-rate-switch-label">Rep rate</span>
                </label>
                <PaymentBadge rep={rep} />
                <span className="opp-rep-yr" title={`Proj: ${rep.projectJobs} | Year: ${rep.totalJobs}`}>
                  {rep.projectJobs}/{rep.totalJobs}
                </span>
              </span>
              <span className="opp-row-right">
                {renderStars(rep.rating)}
              </span>
            </div>

            <div className="opp-card-row3">
              <span className="opp-row-left">
                {rep.phone ? <span className="opp-rep-phone">{rep.phone}</span> : null}
              </span>
              <span className="opp-row-right">
                {rep.otrResAbdnHtml
                  ? <ComplianceBar html={rep.otrResAbdnHtml} />
                  : <span className="opp-na">—</span>}
              </span>
            </div>

            <div className="opp-actions">
              <button
                type="button"
                className={`opp-send-btn${alreadySent ? ' sent' : ''}${rep.fnBlocked ? ' fn-blocked' : ''}`}
                disabled={alreadySent || rep.fnBlocked || isSending}
                onClick={() => handleSend(rep.repId)}
                title={rep.fnBlocked ? 'FN not ready for this ticket' : ''}
              >
                {alreadySent ? '✓ Sent' : isSending ? 'Sending…' : rep.fnBlocked ? 'FN Not Ready' : 'Send Opportunity'}
              </button>
              {alreadySent && (
                onCooldown ? (
                  <button
                    type="button"
                    className="opp-rmd-btn cooldown"
                    disabled
                    title={`Next reminder in ${fmtCooldownLeft(rs.lastMs, cooldownMs)}`}
                  >
                    Reminded ×{rs.count}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="opp-rmd-btn"
                    disabled={isReminding}
                    onClick={() => handleReminder(rep.repId)}
                  >
                    {isReminding
                      ? 'Sending…'
                      : (rs?.count || 0) === 0
                        ? 'Send Reminder'
                        : `Remind again (${rs.count})`}
                  </button>
                )
              )}
            </div>
            {error && <div className="opp-error">{error}</div>}
          </div>
        );
      })}

      {manualOpen && (
        <div className="opp-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) setManualOpen(false); }}>
          <div className="opp-manual-modal" role="dialog" aria-modal="true">
            <div className="opp-modal-header">
              <span className="opp-modal-title">Send Manual Invite</span>
              <button type="button" className="opp-modal-close" onClick={() => setManualOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="opp-manual-search-row">
              <input
                ref={manualInputRef}
                className="opp-manual-search"
                type="search"
                value={manualQuery}
                onChange={e => setManualQuery(e.target.value)}
                placeholder="Rep name, phone, email, or ID"
              />
            </div>
            <div className="opp-manual-results">
              {manualError && <div className="opp-error">{manualError}</div>}
              {manualQuery.trim().length < 2 && !/^\d+$/.test(manualQuery.trim()) ? (
                <div className="opp-manual-msg">Type at least 2 characters, or an exact rep ID.</div>
              ) : manualResults.length === 0 ? (
                <div className="opp-manual-msg">No matching field reps.</div>
              ) : (
                manualResults.map(rep => {
                  const block = manualBlockReason(rep);
                  const sent = invitedSet.has(rep.repId);
                  const sending = manualSendingId === rep.repId;
                  return (
                    <div key={rep.repId} className={`opp-manual-result${block && !sent ? ' blocked' : ''}`}>
                      <div className="opp-manual-rep-main">
                        <a
                          href={`https://siteservicesnow.quickbase.com/db/bjt99p7ie?a=dr&rid=${rep.repId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="opp-rep-name"
                        >
                          {rep.name || `Rep #${rep.repId}`}
                        </a>
                        <div className="opp-manual-meta">#{rep.repId} · {rep.phone || rep.email || 'No contact'}</div>
                      </div>
                      <PaymentBadge rep={rep} />
                      {renderStars(rep.rating)}
                      <button
                        type="button"
                        className={`opp-manual-send${sent ? ' sent' : ''}`}
                        disabled={!!block || sending}
                        onClick={() => handleManualInvite(rep.repId)}
                      >
                        {sent ? 'Sent' : sending ? 'Sending…' : block || 'Send Invite'}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OpportunityPanel;
